import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { AvailabilitySetup } from "lib/features/availability/types";
import { Item } from "lib/features/item/types";
import {
  selectCustomerDetails,
  selectIsExistingBookingLink,
} from "lib/features/message/messageSlice";
import { User } from "lib/features/user/types";
import moment from "moment";
import React from "react";
import { useSelector } from "react-redux";
import { addAvailabilityStyles } from "styles/AvailabilityDirectory/addAvailability.styles";
import { updateEndTime } from "util/commonHelper";
import { getAvatarImageSource, getFullImageSource } from "util/getImageSource";

type ReviewAndSaveStepProps = {
  itemDetails: Item;
  availabilitySetup: AvailabilitySetup | null;
  selectedInfluencer: User | undefined;
  setSelectedInfluencer: React.Dispatch<React.SetStateAction<User | undefined>>;
  isEditing: boolean;
  isLoading: boolean;
  setStep: React.Dispatch<React.SetStateAction<number>>;
  submit: (values: any) => void;
};

const ReviewAndSaveForm = ({
  itemDetails,
  availabilitySetup,
  setStep,
  selectedInfluencer,
  setSelectedInfluencer,
  isLoading,
  submit,
}: ReviewAndSaveStepProps) => {
  const customerDetails = useSelector(selectCustomerDetails);
  const isExistingBookingLink = useSelector(selectIsExistingBookingLink);

  return (
    <div>
      {availabilitySetup?.private && selectedInfluencer && (
        <>
          <Typography>Referee</Typography>
          <Box key={itemDetails?.id} sx={addAvailabilityStyles.card}>
            <Box sx={addAvailabilityStyles.imageContainer}>
              <img
                src={selectedInfluencer?.businessImage ? getFullImageSource(selectedInfluencer?.businessImage) : getAvatarImageSource(selectedInfluencer)}
                style={addAvailabilityStyles.image}
                alt="user-icon"
              />
            </Box>
            <Box sx={addAvailabilityStyles.detailsContainer}>
              <Box sx={addAvailabilityStyles.textContainer}>
                <Typography sx={addAvailabilityStyles.name}>
                  {
                    selectedInfluencer?.business_name ? selectedInfluencer?.business_name : selectedInfluencer?.first_name + " " + selectedInfluencer?.last_name?.[0] + "."
                  }
                </Typography>

                <Typography sx={addAvailabilityStyles.company}>
                  {selectedInfluencer?.influencer_type?.type}
                </Typography>
                <Typography sx={addAvailabilityStyles.location}>
                  {selectedInfluencer?.regions?.[0]?.region?.region}
                </Typography>
              </Box>
            </Box>
          </Box>
        </>
      )}

      {availabilitySetup && availabilitySetup?.times?.length > 0 && (
        <>
          <Typography style={addAvailabilityStyles.reviewHeadingText}>
            {availabilitySetup?.private ? "Date" : "Start Date"}
          </Typography>
          <Typography sx={addAvailabilityStyles.reviewSubHeadingText}>
            {moment
              .utc(availabilitySetup.start_date)
              .local()
              .format("ddd, MMM DD, yyyy")}
          </Typography>
          {!availabilitySetup.private && (
            <>
              {!moment
                .utc(availabilitySetup.start_date)
                .local()
                .isSame(
                  moment.utc(availabilitySetup.end_date).local(),
                  "day"
                ) && (
                  <>
                    <Typography style={addAvailabilityStyles.reviewHeadingText}>
                      End Date
                    </Typography>
                    <Typography sx={addAvailabilityStyles.reviewSubHeadingText}>
                      {moment
                        .utc(availabilitySetup.end_date)
                        .local()
                        .format("ddd, MMM DD, yyyy")}
                    </Typography>
                  </>
                )}

              <Typography style={addAvailabilityStyles.reviewHeadingText}>
                Days Available
              </Typography>
              {availabilitySetup?.days.map((item: string, index: number) => (
                <Typography
                  key={index}
                  sx={addAvailabilityStyles.reviewSubHeadingText}
                >
                  {item.slice(0, 3)}
                </Typography>
              ))}
            </>
          )}
        </>
      )}

      <Typography style={addAvailabilityStyles.reviewHeadingText}>
        {availabilitySetup && availabilitySetup.private === false
          ? "Start Times"
          : "Time"}
      </Typography>

      {availabilitySetup &&
        availabilitySetup.times.map((time: any, index: number) => (
          <Box key={index} sx={addAvailabilityStyles.pointerContainer}>
            <Box style={addAvailabilityStyles.pointerBox} />
            <Typography style={addAvailabilityStyles.reviewSubHeadingText}>
              {moment
                .utc(time.start_time, "YYYY-MM-DDTHH:mm:ss.SSSZ")
                .local()
                .format("hh:mm A")}
              {" to "}
              {moment
                .utc(
                  updateEndTime(
                    time.start_time,
                    itemDetails?.itemDetails?.[0]?.duration
                  ),
                  "YYYY-MM-DDTHH:mm:ss.SSSZ"
                )
                .local()
                .format("hh:mm A")}
              {` `}({itemDetails?.itemDetails?.[0]?.duration}{" "}
              {Number(itemDetails?.itemDetails?.[0]?.duration) > 1
                ? "hours"
                : "hour"}
              )
            </Typography>
          </Box>
        ))}

      {/* Buttons Section */}

      {isExistingBookingLink && customerDetails?.customer_name ? (
        <Button
          variant="contained"
          sx={[
            addAvailabilityStyles.publishContainer,
            addAvailabilityStyles.mt10,
          ]}
          onClick={() => submit(availabilitySetup)}
        >
          {isLoading ? (
            <CircularProgress size={24} sx={{ color: "white" }} />
          ) : (
            "Share"
          )}
        </Button>
      ) : (
        <Box sx={addAvailabilityStyles.buttonContainer}>
          {/* Edit Availability Button */}
          <Button
            variant="outlined"
            sx={addAvailabilityStyles.editButton}
            onClick={() => setStep(1)}
          >
            Edit Availability
          </Button>

          {/* Publish Button */}
          <Button
            variant="contained"
            sx={addAvailabilityStyles.publishContainer}
            onClick={() => submit(availabilitySetup)}
          >
            {isLoading ? (
              <CircularProgress size={24} sx={{ color: "white" }} />
            ) : availabilitySetup?.private ? (
              "Share Booking Link"
            ) : (
              "Publish"
            )}
          </Button>
        </Box>
      )}
    </div>
  );
};

export default ReviewAndSaveForm;